/**
 * Navbar.js
 * Full nav: Home | Products | Categories ▼ | Safety | About Us | Contact | 🛒 Shop Now
 * Categories dropdown with all 10 categories + hover reveal
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useCart }  from '../../context/CartContext';
import { useAuth }  from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/* ── All categories ─────────────────────────────────────────────────────────── */
const CATEGORIES = [
  { slug: 'one-sound', name: 'One Sound Crackers', icon: '💥', color: 'text-orange-400' },
  { slug: 'wala-items', name: 'Wala Items', icon: '🎵', color: 'text-pink-400' },
  { slug: 'bomb', name: 'Bomb', icon: '💣', color: 'text-gray-400' },
  { slug: 'flower-pots', name: 'Flower Pots', icon: '🌸', color: 'text-pink-400' },
  { slug: 'ground', name: 'Ground Chakkar', icon: '🌀', color: 'text-teal-400' },
  { slug: 'candles', name: 'Candles', icon: '🕯️', color: 'text-yellow-400' },
  { slug: 'rockets', name: 'Rockets', icon: '🚀', color: 'text-red-400' },
  { slug: 'fountains', name: 'Fountains', icon: '⛲', color: 'text-blue-400' },
  { slug: 'showers', name: 'Showers', icon: '🌟', color: 'text-cyan-400' },
  { slug: 'premium-products', name: 'Premium Products', icon: '👑', color: 'text-amber-400' },
  { slug: 'sky-shots', name: 'Sky Shots', icon: '🎆', color: 'text-indigo-400' },
  { slug: 'repeating-skyshots', name: 'Repeating Skyshots', icon: '🎇', color: 'text-violet-400' },
  { slug: 'sparklers', name: 'Sparklers', icon: '✨', color: 'text-amber-400' },
  { slug: 'matches', name: 'Matches', icon: '🔥', color: 'text-red-400' },
  { slug: 'gift-boxes', name: 'Gift Boxes', icon: '🎁', color: 'text-emerald-400' },
  { slug: 'combo-packs', name: 'Combo Items', icon: '🎊', color: 'text-purple-400' },
  { slug: 'kids-special', name: 'Kids Special', icon: '🎈', color: 'text-pink-400' },
];

/* ── Categories dropdown ─────────────────────────────────────────────────────── */
function CategoriesDropdown({ linkClass }) {
  const [open,  setOpen]  = useState(false);
  const [touch, setTouch] = useState(false);
  const navigate          = useNavigate();
  const ref               = useRef(null);
  const timerRef          = useRef(null);

  const openMenu  = () => { clearTimeout(timerRef.current); setOpen(true);  };
  const closeMenu = () => { timerRef.current = setTimeout(() => setOpen(false), 120); };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCategoryClick = (slug) => {
    setOpen(false);
    navigate(`/products?category=${slug}`);
  };

  return (
    <div ref={ref} className="relative"
      onMouseEnter={() => { if (!touch) openMenu(); }}
      onMouseLeave={() => { if (!touch) closeMenu(); }}>

      {/* Trigger */}
      <button
        onClick={() => { setTouch(true); setOpen(o => !o); }}
        className={`flex items-center gap-1.5 ${linkClass({ isActive: false })}`}>
        Categories
        <svg
          className={`w-3 h-3 transition-transform duration-200 opacity-70 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 z-50
            bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden
            animate-scale-in origin-top">

          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-red-950 to-red-800 flex items-center justify-between">
            <p className="text-[10px] font-black text-yellow-400/80 uppercase tracking-widest">
              Shop by Category
            </p>
            <button onClick={() => { setOpen(false); navigate('/products'); }}
              className="text-red-300/60 hover:text-white text-[10px] font-bold transition-colors">
              View All →
            </button>
          </div>

          {/* Category list — 2 columns */}
          <div className="grid grid-cols-2 gap-0.5 p-2 bg-gray-50">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left
                  hover:bg-white hover:shadow-sm
                  transition-all duration-150 group">
                <span className="text-base flex-shrink-0">{cat.icon}</span>
                <span className="text-xs font-semibold text-gray-700 group-hover:text-red-700
                  leading-tight transition-colors">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="px-3 py-2.5 border-t border-gray-100 bg-white">
            <button onClick={() => { setOpen(false); navigate('/products'); }}
              className="w-full text-center text-xs font-black text-red-600 hover:text-red-800
                py-1.5 rounded-lg hover:bg-red-50 transition-all">
              Browse All Products →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Navbar ─────────────────────────────────────────────────────────────── */
export default function Navbar() {
  const { itemCount }    = useCart();
  const { user, logout } = useAuth();
  const { toast }        = useToast();
  const navigate         = useNavigate();
  const location         = useLocation();

  const [open,     setOpen]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartBump, setCartBump] = useState(false);
  const [glowing,  setGlowing]  = useState(false);
  const [catOpen,  setCatOpen]  = useState(false); // mobile category accordion

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Periodic glow pulse on brand
  useEffect(() => {
    const t = setInterval(() => {
      setGlowing(true);
      setTimeout(() => setGlowing(false), 900);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const prevCount = React.useRef(itemCount);
  useEffect(() => {
    if (itemCount > prevCount.current) { setCartBump(true); setTimeout(() => setCartBump(false), 600); }
    prevCount.current = itemCount;
  }, [itemCount]);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); setCatOpen(false); }, [location]);

  const handleLogout = () => {
    logout();
    toast.success('See you next Diwali! 🪔');
    navigate('/');
  };

  const linkClass = ({ isActive }) =>
    `relative font-semibold text-xs transition-colors duration-200 py-1 cursor-pointer
     ${isActive ? 'text-yellow-300' : 'text-red-100/90 hover:text-white'}
     after:absolute after:bottom-0 after:left-0 after:h-0.5 after:rounded-full
     after:bg-yellow-400 after:transition-all after:duration-300
     ${isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'}`;

  return (
    <nav className={`sticky top-0 z-40 transition-all duration-500
      ${scrolled
        ? 'bg-red-900/97 backdrop-blur-xl shadow-2xl shadow-red-900/50 border-b border-yellow-400/15'
        : 'bg-gradient-to-r from-red-950 via-red-900 to-red-800'
      }`}>

      {/* Gold shimmer bottom line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r
        from-transparent via-yellow-400/50 to-transparent opacity-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[62px] gap-4">

          {/* ── Brand ── */}
          <Link to="/" className="flex items-center gap-2 group shrink-0 select-none">
            <div className="relative flex-shrink-0">
              <img
                src="/logo.png"
                alt="Lakshmi Crackers"
                className={`h-12 w-12 object-contain transition-all duration-500
                  group-hover:scale-110 ${glowing ? 'scale-105' : ''}`}
                style={{
                  filter: glowing
                    ? 'drop-shadow(0 0 14px #f5c842) drop-shadow(0 0 28px rgba(245,200,66,0.7))'
                    : 'drop-shadow(0 0 8px rgba(245,200,66,0.6))',
                  transition: 'filter 0.4s ease, transform 0.3s ease',
                }}/>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-black text-[16px] uppercase transition-all duration-300"
                style={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  letterSpacing: '0.04em',
                  color: glowing ? '#fff7aa' : '#fde68a',
                  textShadow: glowing
                    ? '0 0 12px rgba(245,200,66,0.9), 0 0 24px rgba(245,200,66,0.5)'
                    : '0 0 8px rgba(245,200,66,0.4)',
                }}>
                Lakshmi Crackers
              </span>
              <span className="text-[8px] font-semibold text-yellow-300/55 uppercase tracking-[0.2em] mt-0.5 hidden sm:block">
                Where every spark speaks joy!
              </span>
            </div>
          </Link>

          {/* ── Desktop nav links ── */}
          <div className="hidden lg:flex items-center gap-3 flex-1 justify-center">
            <NavLink to="/" end className={linkClass}>Home</NavLink>
            <NavLink to="/products"  className={linkClass}>Products</NavLink>

            {/* Categories dropdown */}
            <CategoriesDropdown linkClass={linkClass} />

            <NavLink to="/about"    className={linkClass}>About Us</NavLink>
            <NavLink to="/contact"     className={linkClass}>Contact</NavLink>
            <NavLink to="/track-order" className={linkClass}>Track Order</NavLink>
            <a href="/pricelist.pdf" target="_blank" rel="noreferrer"
              className={linkClass({ isActive: false })}>
              Price List
            </a>


            {user?.is_staff && (
              <NavLink to="/admin"
                className={({ isActive }) =>
                  `text-xs font-bold px-3 py-1.5 rounded-lg transition-all duration-200
                   ${isActive
                     ? 'bg-yellow-400 text-red-900'
                     : 'bg-white/10 text-yellow-300/90 hover:bg-yellow-400/20 hover:text-yellow-300 border border-yellow-400/20'}`}>
                ⚙ Admin
              </NavLink>
            )}
          </div>

          {/* ── Right actions ── */}
          <div className="flex items-center gap-1 flex-shrink-0">

            {/* Cart icon */}
            <Link to="/cart"
              className="relative p-2 text-red-200 hover:text-white transition-colors rounded-xl hover:bg-white/10"
              title="Shopping Cart">
              <svg className={`w-5 h-5 transition-transform duration-300 ${cartBump ? 'scale-125 text-yellow-400' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.3 2.3c-.6.6-.2 1.7.7 1.7H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              {itemCount > 0 && (
                <span className={`absolute -top-0.5 -right-0.5
                  bg-gradient-to-br from-yellow-400 to-amber-500 text-red-900 text-[10px] font-black
                  min-w-[18px] h-[18px] px-0.5 flex items-center justify-center rounded-full
                  shadow-lg transition-all duration-300 ${cartBump ? 'scale-125' : ''}`}>
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>

            {/* ── SHOP NOW — prominent CTA button ── */}
            <Link to="/products"
              className="hidden sm:flex items-center gap-1.5
                bg-gradient-to-br from-yellow-400 via-amber-400 to-yellow-500
                text-red-900 font-black text-sm px-4 py-2 rounded-xl
                shadow-lg shadow-yellow-400/40
                hover:shadow-yellow-400/60 hover:from-yellow-300 hover:to-amber-400
                hover:-translate-y-0.5 active:scale-95
                transition-all duration-200 border border-yellow-300/30
                relative overflow-hidden group">
              {/* Shimmer sweep */}
              <span className="absolute inset-0 translate-x-[-110%] group-hover:translate-x-[110%]
                transition-transform duration-500 bg-gradient-to-r
                from-transparent via-white/35 to-transparent skew-x-12 pointer-events-none" />
              <span className="relative">🛒</span>
              <span className="relative">Shop Now</span>
            </Link>

            {/* Auth — desktop only */}
            {user ? (
              <div className="hidden lg:flex items-center gap-1.5">
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-white/8 border border-white/10">
                  <div className="w-6 h-6 bg-gradient-to-br from-yellow-400 to-amber-500 text-red-900 rounded-full
                    flex items-center justify-center font-black text-xs shadow">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-red-100/80 text-xs font-semibold max-w-[70px] truncate">
                    {user.name?.split(' ')[0]}
                  </span>
                </div>
                <button onClick={handleLogout}
                  className="text-red-300/70 hover:text-white text-xs font-semibold
                    px-2.5 py-1.5 rounded-xl hover:bg-white/10 transition-all">
                  Logout
                </button>
              </div>
            ) : (
              <div className="hidden lg:flex items-center gap-1">
                <Link to="/login"
                  className="text-red-200/80 hover:text-white font-semibold text-xs px-3 py-2 rounded-xl hover:bg-white/10 transition-all">
                  Login
                </Link>
                <Link to="/register"
                  className="bg-white/10 border border-white/20 hover:bg-white/18
                    text-yellow-200 font-bold text-xs px-3 py-2 rounded-xl transition-all">
                  Register
                </Link>
              </div>
            )}

            {/* Mobile hamburger */}
            <button className="lg:hidden p-2 text-red-200 hover:text-white hover:bg-white/10 rounded-xl transition-all"
              onClick={() => setOpen(o => !o)} aria-label="Toggle navigation menu" aria-expanded={open}>
              <div className="w-5 h-3.5 flex flex-col justify-between">
                <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 origin-center ${open ? 'rotate-45 translate-y-[6px]' : ''}`}/>
                <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${open ? 'opacity-0 scale-x-0' : ''}`}/>
                <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 origin-center ${open ? '-rotate-45 -translate-y-[6px]' : ''}`}/>
              </div>
            </button>
          </div>
        </div>

        {/* ── Mobile menu ── */}
        <div className={`lg:hidden overflow-hidden transition-all duration-400
          ${open ? 'max-h-[600px] opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
          <div className="border-t border-white/10 pt-3 space-y-0.5">

            {/* Core nav links */}
            {[
              { to:'/', label:'🏠 Home', end:true },
              { to:'/products', label:'🎆 Products' },
              { to:'/safety',   label:'🛡️ Safety' },
              { to:'/about',    label:'ℹ️ About Us' },
              { to:'/contact',     label:'📞 Contact' },
      { to:'/track-order', label:'🔍 Track Order' },
              ...(user?.is_staff ? [{ to:'/admin', label:'⚙️ Admin Panel' }] : []),
            ].map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) =>
                  `block px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
                   ${isActive ? 'bg-yellow-400/20 text-yellow-300' : 'text-red-100/80 hover:bg-white/8 hover:text-white'}`}>
                {label}
              </NavLink>
            ))}

            {/* Mobile Categories accordion */}
            <div>
              <button onClick={() => setCatOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl
                  text-sm font-semibold text-red-100/80 hover:bg-white/8 hover:text-white transition-all">
                <span>🏷️ Categories</span>
                <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              {catOpen && (
                <div className="mt-1 ml-4 grid grid-cols-2 gap-1 pb-1">
                  {CATEGORIES.map(cat => (
                    <NavLink key={cat.slug} to={`/products?category=${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold
                        text-red-100/70 hover:bg-white/8 hover:text-white transition-all">
                      <span>{cat.icon}</span>{cat.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Price List — mobile */}
            <a href="/pricelist.pdf" target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                text-red-100/80 hover:bg-white/8 hover:text-white transition-all">
              📄 Price List
            </a>

            {/* Shop Now — mobile */}
            <div className="px-2 pt-1">
              <Link to="/products"
                className="flex items-center justify-center gap-2
                  bg-gradient-to-br from-yellow-400 to-amber-500 text-red-900
                  font-black py-3 rounded-2xl text-sm shadow-lg shadow-yellow-400/30
                  hover:-translate-y-0.5 active:scale-95 transition-all">
                🛒 Shop Now
              </Link>
            </div>

            {/* Auth */}
            <div className="border-t border-white/10 pt-3 mt-1 px-2">
              {user ? (
                <div className="flex items-center justify-between">
                  <span className="text-red-200/70 text-sm">{user.name}</span>
                  <button onClick={handleLogout}
                    className="text-yellow-300 text-sm font-bold px-3 py-1 rounded-lg hover:bg-yellow-400/20 transition-all">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" className="flex-1 text-center text-red-100/80 font-semibold text-sm
                    px-4 py-2.5 rounded-xl hover:bg-white/10 transition-all border border-white/10">
                    Login
                  </Link>
                  <Link to="/register" className="flex-1 text-center bg-gradient-to-br from-yellow-400
                    to-amber-500 text-red-900 font-bold text-sm px-4 py-2.5 rounded-xl shadow">
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
